import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader, Link2, AlertCircle, Search } from 'lucide-react';

import { eventTeamApiService } from '../../services/eventTeamApi';
import { kanbanApiService } from '../../services/kanbanApi';
import { useAuth } from '../../contexts/AuthContext';
import { useDebounce } from '../../hooks/useDebounce';
import {
  kanbanTeamsFromPermissions,
  kanbanTeamsFromStaffTeams,
  mergeKanbanTeamLists,
} from '../../utils/kanbanPermissions';
import { teamStaffOnlyKanbanWrite } from '../../utils/tesseractTeamAccess';
import { cn } from '../../utils/cn';
import type { KanbanBoard, KanbanCardSummary } from '../../types/kanban';
import type { EventKanbanLink } from '../../types/eventTeam';

type SyncFlagName =
  | 'sync_status'
  | 'sync_priority'
  | 'sync_due_date'
  | 'sync_assignees'
  | 'sync_comments'
  | 'propagate_watchers';

const SYNC_FLAG_LABELS: Record<SyncFlagName, string> = {
  sync_status: 'Status',
  sync_priority: 'Priority',
  sync_due_date: 'Due date',
  sync_assignees: 'Assignees',
  sync_comments: 'Comments',
  propagate_watchers: 'Watchers',
};

export interface LinkEventCardModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventId: string;
  /** Cards already bridged (hidden from the picker). */
  linkedCardIds: string[];
  onSuccess: (link: EventKanbanLink) => void;
}

/** Link an event to an EXISTING kanban card with per-link sync flags (guide §1.5). */
export const LinkEventCardModal = ({ isOpen, onClose, eventId, linkedCardIds, onSuccess }: LinkEventCardModalProps) => {
  const { identity } = useAuth();
  const isDev = import.meta.env.DEV;

  const writableTeams = useMemo(() => {
    if (!identity && !isDev) return [];
    const permissions = Array.isArray(identity?.raw?.custom?.permissions)
      ? (identity!.raw.custom.permissions as string[])
      : [];
    const groups = Array.isArray(identity?.raw?.custom?.groups)
      ? (identity!.raw.custom.groups as string[])
      : [];
    const isSuper = isDev || groups.includes('SPY_Super_admin') || permissions.includes('argus.kanban.super');
    const fromPermissions = kanbanTeamsFromPermissions(permissions);
    const fromStaff = kanbanTeamsFromStaffTeams(identity?.raw?.custom?.staff_teams);
    const all = mergeKanbanTeamLists(fromPermissions, fromStaff);
    if (isSuper) return all.length > 0 ? all : ['devteam', 'buildteam', 'eventteam', 'management'];
    return all.filter(
      (t) =>
        permissions.includes(`argus.kanban.write.${t}`) ||
        permissions.includes(`argus.kanban.manage.${t}`) ||
        teamStaffOnlyKanbanWrite(identity, isDev, t),
    );
  }, [identity, isDev]);

  const [team, setTeam] = useState('');
  const [boards, setBoards] = useState<KanbanBoard[]>([]);
  const [boardId, setBoardId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 300);
  const [results, setResults] = useState<KanbanCardSummary[]>([]);
  const [searching, setSearching] = useState(false);
  const [notes, setNotes] = useState('');
  const [syncFlags, setSyncFlags] = useState<Record<SyncFlagName, boolean>>({
    sync_status: true,
    sync_priority: true,
    sync_due_date: true,
    sync_assignees: true,
    sync_comments: true,
    propagate_watchers: true,
  });
  const [submittingCardId, setSubmittingCardId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setSearchQuery('');
    setResults([]);
    if (!team && writableTeams.length > 0) {
      setTeam(writableTeams.includes('eventteam') ? 'eventteam' : writableTeams[0]!);
    }
  }, [isOpen, writableTeams, team]);

  useEffect(() => {
    if (!isOpen || !team) {
      setBoards([]);
      return;
    }
    let cancelled = false;
    kanbanApiService
      .listBoards({ team, limit: 100 })
      .then((res) => {
        if (!cancelled) setBoards(Array.isArray(res.results) ? res.results : []);
      })
      .catch(() => {
        if (!cancelled) setBoards([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, team]);

  useEffect(() => {
    if (!isOpen || debouncedSearch.trim().length === 0) {
      setResults([]);
      return;
    }
    let cancelled = false;
    setSearching(true);
    kanbanApiService
      .searchCards({ q: debouncedSearch, team: team || undefined, board_id: boardId || undefined, limit: 25 })
      .then((res) => {
        if (!cancelled) setResults(Array.isArray(res.results) ? res.results : []);
      })
      .catch(() => {
        if (!cancelled) setResults([]);
      })
      .finally(() => {
        if (!cancelled) setSearching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, debouncedSearch, team, boardId]);

  const handleLink = async (cardId: string) => {
    setSubmittingCardId(cardId);
    setError(null);
    try {
      const link = await eventTeamApiService.createEventKanbanLink(eventId, {
        card_id: cardId,
        notes: notes.trim() || null,
        ...syncFlags,
      });
      onSuccess(link);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to link card');
    } finally {
      setSubmittingCardId(null);
    }
  };

  if (!isOpen) return null;

  const linkedSet = new Set(linkedCardIds);
  const visibleResults = results.filter((c) => !linkedSet.has(c.id));

  const modalContent = (
    <div className="modal-overlay z-[9999]">
      <div className="modal-panel max-w-xl">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 text-glass-400 hover:text-white p-2"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <h2 className="text-lg font-semibold tracking-tight text-white mb-4 flex items-center gap-2">
          <Link2 className="w-6 h-6 text-accent-400" />
          Link existing card
        </h2>

        {error && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 mb-4">
            <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
            <span className="text-sm text-red-300">{error}</span>
          </div>
        )}

        {writableTeams.length === 0 ? (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0" />
            <span className="text-sm text-amber-300">
              You don't have kanban write access to any team (need <code>argus.kanban.write.eventteam</code>).
            </span>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-glass-300 mb-1 block">Team</label>
                <select
                  value={team}
                  onChange={(e) => {
                    setTeam(e.target.value);
                    setBoardId('');
                  }}
                  className="glass-input w-full text-sm"
                >
                  {writableTeams.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm text-glass-300 mb-1 block">Board (optional)</label>
                <select value={boardId} onChange={(e) => setBoardId(e.target.value)} className="glass-input w-full text-sm">
                  <option value="">Any board</option>
                  {boards.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.title}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search cards…"
                className="glass-input w-full pl-10 text-sm"
                autoFocus
              />
              {searching && (
                <Loader className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-glass-400" />
              )}
            </div>

            {visibleResults.length > 0 && (
              <div className="space-y-1 max-h-[220px] overflow-y-auto">
                {visibleResults.map((card) => (
                  <button
                    key={card.id}
                    onClick={() => void handleLink(card.id)}
                    disabled={submittingCardId != null}
                    className="w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-md text-left hover:bg-glass-200/10 transition-colors"
                  >
                    <span className="text-sm text-white truncate">{card.title}</span>
                    {submittingCardId === card.id ? (
                      <Loader className="w-3.5 h-3.5 animate-spin text-glass-400 flex-shrink-0" />
                    ) : (
                      <Link2 className="w-3.5 h-3.5 text-accent-400 flex-shrink-0" />
                    )}
                  </button>
                ))}
              </div>
            )}
            {debouncedSearch.trim().length > 0 && !searching && visibleResults.length === 0 && (
              <p className="text-xs text-glass-500 text-center py-2">No unlinked cards match.</p>
            )}

            <div>
              <label className="text-sm text-glass-300 mb-1 block">Bridge notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder="Optional context for this bridge…"
                className="glass-input w-full text-sm"
              />
            </div>

            <div>
              <p className="text-xs text-glass-400 mb-1.5">Sync flags for the new bridge:</p>
              <div className="flex flex-wrap gap-1.5">
                {(Object.keys(SYNC_FLAG_LABELS) as SyncFlagName[]).map((key) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setSyncFlags((prev) => ({ ...prev, [key]: !prev[key] }))}
                    className={cn(
                      'inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium border transition-all',
                      syncFlags[key]
                        ? 'bg-accent-500/20 border-accent-500/40 text-accent-200'
                        : 'bg-glass-100/5 border-glass-200/20 text-glass-400 hover:text-white',
                    )}
                  >
                    {SYNC_FLAG_LABELS[key]}
                    <span className="font-mono text-[9px] opacity-70">{syncFlags[key] ? 'on' : 'off'}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
